import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Field } from '@vnetwork/ui/forms';
import { Button, Input, Textarea, Checkbox, Skeleton, Card, CardHeader, CardTitle, CardDescription, CardBody } from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useServiceAdvancedStrategy,
  useServiceAdvancedEnabledSites,
  useCreateServiceCompliance,
  useUpdateServiceCompliance,
  COMPLIANCE_TYPE,
  type AdvancedStrategy,
} from '@/api/waap';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';
import { errMessage } from '@/pages/services/waap/waap-util';
import { trackFeatureAction } from '@/lib/analytics-events';
import styles from './ComplianceForm.module.css';

const NAME_MAX = 26;
const DESC_MAX = 200;
const TYPE = COMPLIANCE_TYPE;

/** The HTTP-protocol numeric limits: each a `<key>` value gated by a
 *  `<key>_enabled` checkbox. Order mirrors the portal's 3-column grid. */
const LIMIT_FIELDS = [
  { key: 'url_length', label: 'Max Length of URL', def: 4096 },
  { key: 'user_agent_length', label: 'Max Length of User Agent', def: 4096 },
  { key: 'request_header_num', label: 'Max Number of Request Headers', def: 32 },
  { key: 'request_header_name_length', label: 'Max Length of Request Header Name', def: 64 },
  { key: 'request_header_value_length', label: 'Max Length of Request Header Value', def: 128 },
  { key: 'url_args_num', label: 'Max Number of Request URL Args', def: 20 },
  { key: 'args_length', label: 'Max Length of Request Args', def: 8192 },
  { key: 'post_body_args_num', label: 'Max Number of POST Body Args', def: 256 },
  { key: 'referer_length', label: 'Max Length of Referer', def: 4096 },
  { key: 'accept_charset_length', label: 'Max Length of Accept-Charset', def: 128 },
  { key: 'content_length', label: 'Max Value of Content-Length', def: 16384000 },
  { key: 'cookie_length', label: 'Max Length of Cookie', def: 1024 },
  { key: 'cookie_num_limit', label: 'Max Number of Cookies', def: 64 },
  { key: 'accept_length', label: 'Max Length of Accept', def: 1024 },
  { key: 'range_number', label: 'Max Number of Ranges', def: 5 },
  { key: 'range_length', label: 'Max Value of Range', def: 5242880 },
] as const;

/** The Compliance (type 8) per-type config (flat limits + repeat_http_header). */
type ComplianceConfig = Record<string, unknown>;

interface FormState {
  name: string;
  description: string;
  sites: number[];
  values: Record<string, number>;
  enabled: Record<string, boolean>;
  repeat_http_header: boolean;
}

const emptyForm = (): FormState => ({
  name: '',
  description: '',
  sites: [],
  values: Object.fromEntries(LIMIT_FIELDS.map((f) => [f.key, f.def])),
  enabled: Object.fromEntries(LIMIT_FIELDS.map((f) => [f.key, false])),
  repeat_http_header: false,
});

/**
 * Add / Edit an HTTP Protocol Compliance rule (AI-WAF → Compliance Validation,
 * strategy type 8). Reads via the advanced-strategy endpoint; writes via the
 * separate waf_compliance surface. Routes: aiwaf/compliance/new + /:id.
 */
export function ComplianceForm() {
  const navigate = useNavigate();
  const { serviceId, id } = useParams<{ serviceId: string; id: string }>();
  const isEdit = !!id;
  const { show } = useToast();
  const base = `/services/waap/${serviceId}`;

  const { data: existing, isLoading: loadingExisting } = useServiceAdvancedStrategy<ComplianceConfig>(
    serviceId,
    TYPE,
    id,
  );
  const { data: sites = [] } = useServiceAdvancedEnabledSites(serviceId);

  const [form, setForm] = useState<FormState>(emptyForm);
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  useEffect(() => {
    if (!existing) return;
    const c = (existing.config ?? {}) as ComplianceConfig;
    const num = (k: string, d: number) => (typeof c[k] === 'number' ? (c[k] as number) : d);
    setForm({
      name: existing.name ?? '',
      description: existing.description ?? '',
      sites: existing.sites ?? [],
      values: Object.fromEntries(LIMIT_FIELDS.map((f) => [f.key, num(f.key, f.def)])),
      enabled: Object.fromEntries(LIMIT_FIELDS.map((f) => [f.key, c[`${f.key}_enabled`] === true])),
      repeat_http_header: c.repeat_http_header === true,
    });
  }, [existing]);

  const siteSet = useMemo(() => new Set(form.sites), [form.sites]);
  const toggleSite = (sid: number) =>
    set('sites', siteSet.has(sid) ? form.sites.filter((x) => x !== sid) : [...form.sites, sid]);

  const create = useCreateServiceCompliance<ComplianceConfig>(serviceId ?? '', {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'compliance_rule', 'create');
      show({ tone: 'success', title: 'Compliance rule created' });
      navigate(`${base}/aiwaf/compliance`);
    },
    onError: (e) => show({ tone: 'danger', title: 'Create failed', description: errMessage(e) }),
  });
  const update = useUpdateServiceCompliance<ComplianceConfig>(serviceId ?? '', id ?? '', {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'compliance_rule', 'edit');
      show({ tone: 'success', title: 'Compliance rule saved' });
      navigate(`${base}/aiwaf/compliance`);
    },
    onError: (e) => show({ tone: 'danger', title: 'Save failed', description: errMessage(e) }),
  });
  const saving = create.isPending || update.isPending;

  const nameValid = form.name.trim().length > 0 && form.name.trim().length <= NAME_MAX;
  const canSubmit = nameValid && !saving;

  const submit = () => {
    const config: ComplianceConfig = { repeat_http_header: form.repeat_http_header };
    for (const f of LIMIT_FIELDS) {
      config[f.key] = form.values[f.key];
      config[`${f.key}_enabled`] = form.enabled[f.key];
    }
    const payload: AdvancedStrategy<ComplianceConfig> = {
      type: TYPE,
      name: form.name.trim(),
      description: form.description.trim(),
      sites: form.sites,
      config,
    };
    if (isEdit) update.mutate(payload);
    else create.mutate(payload);
  };

  if (isEdit && loadingExisting) {
    return (
      <Container size="xl">
        <Skeleton style={{ height: 320 }} />
      </Container>
    );
  }

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={
          <WaapConsoleBreadcrumbs
            trail={[
              { label: 'AI-WAF' },
              { label: 'Compliance Validation', slug: 'aiwaf/compliance' },
              { label: isEdit ? 'Edit Compliance Rule' : 'HTTP Protocol Compliance Rule' },
            ]}
          />
        }
        title="HTTP Protocol Compliance Rule"
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
        <Card flat>
          <CardHeader>
            <CardTitle>General</CardTitle>
          </CardHeader>
          <CardBody>
            <div className={styles.formGrid}>
              <Field
                label="Name"
                required
                description={`1-${NAME_MAX} characters. Valid: 0-9, A-Z, a-z, space, . _ - ( ) * +`}
              >
                {(props) => (
                  <Input
                    {...props}
                    value={form.name}
                    maxLength={NAME_MAX}
                    placeholder="Rule name"
                    onChange={(e) => set('name', e.target.value)}
                  />
                )}
              </Field>
              <Field label="Description" description={`Up to ${DESC_MAX} characters.`}>
                {(props) => (
                  <div>
                    <Textarea
                      {...props}
                      value={form.description}
                      maxLength={DESC_MAX}
                      placeholder="Description"
                      onChange={(e) => set('description', e.target.value)}
                    />
                    <div className={styles.charCount}>
                      {form.description.length}/{DESC_MAX}
                    </div>
                  </div>
                )}
              </Field>
            </div>
          </CardBody>
        </Card>

        <Card flat>
          <CardHeader>
            <CardTitle>Triggering Condition</CardTitle>
            <CardDescription>Enable a limit and set its threshold; requests that exceed an enabled limit are blocked.</CardDescription>
          </CardHeader>
          <CardBody>
            <div className={styles.limitBox}>
              <div className={styles.limitGrid}>
                {LIMIT_FIELDS.map((f) => (
                  <div key={f.key}>
                    <label className={styles.checkLabel}>
                      <Checkbox
                        checked={form.enabled[f.key]}
                        onCheckedChange={(v) => set('enabled', { ...form.enabled, [f.key]: !!v })}
                      />
                      <span className={styles.labelText}>{f.label}</span>
                    </label>
                    <Input
                      type="number"
                      min={0}
                      value={String(form.values[f.key])}
                      onChange={(e) =>
                        set('values', { ...form.values, [f.key]: Number(e.target.value) })
                      }
                    />
                  </div>
                ))}
              </div>

              <label className={styles.checkRow}>
                <Checkbox
                  checked={form.repeat_http_header}
                  onCheckedChange={(v) => set('repeat_http_header', !!v)}
                />
                <span>No Duplicate Headers</span>
              </label>
            </div>
          </CardBody>
        </Card>

        <Card flat>
          <CardHeader>
            <CardTitle>Apply to</CardTitle>
            <CardDescription>Select the websites this rule protects.</CardDescription>
          </CardHeader>
          <CardBody>
            {sites.length === 0 ? (
              <div className={styles.muted}>
                No protection-enabled websites available.
              </div>
            ) : (
              <div className={styles.siteList}>
                {sites.map((s) => (
                  <label key={s.id} className={styles.checkRow}>
                    <Checkbox checked={siteSet.has(s.id)} onCheckedChange={() => toggleSite(s.id)} />
                    <span>{s.domain}</span>
                  </label>
                ))}
              </div>
            )}
          </CardBody>
        </Card>

        <div className={styles.formActions}>
          <Button variant="secondary" onClick={() => navigate(`${base}/aiwaf/compliance`)}>
            Cancel
          </Button>
          <Button disabled={!canSubmit} onClick={submit}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </Container>
  );
}
